import * as React from "react";
import { MsalProvider } from "@azure/msal-react";
import type { Configuration } from "@azure/msal-browser";
import { PublicClientApplication } from "@azure/msal-browser";
import type { History } from "history";
import { useHistory } from "react-router-dom";

import { CustomNavigationClient } from "common/context/auth/msal/navigation-client";
import { usePromise } from "common/hooks/use-promise";
import { rest } from "common/api-client/rest";
import apiURL from "common/config/api-url.config";
import { FullPageSpinner } from "common/components/full-page/full-page-spinner";
import { FullPageErrorFallback } from "common/components/full-page/full-page-error-fallback";

type MSALLSettings = {
  clientId: string;
  tenantId: string;
};

type SuccessfulMSALSettingsResponse = MSALLSettings & {
  validation: true;
};
type MSALSettingsResponse =
  | SuccessfulMSALSettingsResponse
  | { validation: false; message?: string };

async function getMSALSettings(history: History) {
  const result = await rest<MSALSettingsResponse>("auth/msalSettings", {
    baseURL: apiURL,
  });
  if (result instanceof Error) {
    throw result;
  }
  if (result.kind === "empty") {
    throw new Error("Response does not have a body");
  }
  if (!result.body.validation) {
    throw new Error(`Validation failed: ${result.body.message}`);
  }

  const MSALConfiguration: Configuration = {
    auth: {
      clientId: result.body.clientId,
      authority: `https://login.microsoftonline.com/${result.body.tenantId}`,
      navigateToLoginRequestUrl: false,
    },
  };

  const pca = new PublicClientApplication(MSALConfiguration);

  const navigationClient = new CustomNavigationClient(history);
  pca.setNavigationClient(navigationClient);

  return pca;
}

export function CustomMSALProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const history = useHistory();

  const { promiseState, run } = usePromise<PublicClientApplication>();

  React.useEffect(() => {
    const MSALSettingsPromise = getMSALSettings(history);
    run(MSALSettingsPromise);
  }, [history, run]);

  if (promiseState.status === "pending" || promiseState.status === "idle") {
    return <FullPageSpinner />;
  }

  if (promiseState.status === "rejected") {
    return <FullPageErrorFallback message={promiseState.error.message} />;
  }

  if (promiseState.status === "resolved") {
    return <MsalProvider instance={promiseState.data}>{children}</MsalProvider>;
  }

  return (
    <div>If this message is displayed there is an unhandled promise status</div>
  );
}
